import environment from "@app/config/environment";
import { type BaseWebsocketMessage, type WebsocketAction } from "@globals";
import { type GenericSchema, any, object, optional, picklist, safeParse } from "valibot";

const websocketActions: WebsocketAction[] = [
  "authentication",
  "activity.created",
  "experiment.update",
  "landscape.chart",
  "notification.ping",
  "notification.pong",
  "prediction.explanation.chart"
];

const messageSchema = object({
  action: picklist(websocketActions),
  payload: optional(any())
}) satisfies GenericSchema<BaseWebsocketMessage>;

class WebSocketClient extends EventTarget {
  private readonly pingInterval = 5000;
  private readonly reconnectDelay = 2000;
  private ws: WebSocket | null = null;
  private pingTimer: number | null = null;
  private reconnectTimeout: number | null = null;
  private shouldReconnect = true;
  private getToken: (() => Promise<string>) | null = null;

  public async initialize(getToken: () => Promise<string>) {
    this.getToken = getToken;
    this.shouldReconnect = true;
    return this.connect();
  }

  private async getUrl() {
    if (!this.getToken) {
      throw new Error("WebSocket not initialized: getToken is not set");
    }
    const token = await this.getToken();
    return new URL(`${environment.ATI_API_BASE_URL}/ws/?token=Bearer ${token}`);
  }

  private async connect() {
    try {
      const url = await this.getUrl();

      return new Promise<void>(resolve => {
        this.ws = new WebSocket(url);

        this.ws.addEventListener("open", this.onOpen.bind(this));
        this.ws.addEventListener("message", this.onMessage.bind(this));
        this.ws.addEventListener("close", this.onClose.bind(this));
        this.ws.addEventListener("error", this.onError.bind(this));
        this.ws.addEventListener("open", () => resolve(), { once: true });
      });
    } catch (error) {
      this.handleDisconnection("Failed to connect to WebSocket", error);
    }
  }

  private onOpen(event: Event) {
    // Ignore events from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log("✅ Connected to WebSocket server");

    // Start ping interval
    this.startPing();
  }

  private onMessage(event: MessageEvent) {
    // Ignore messages from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }
    if (typeof event.data !== "string") {
      // eslint-disable-next-line no-console
      console.error("❌ WebSocket message data is not a string");
      return;
    }

    try {
      const data = safeParse(messageSchema, JSON.parse(event.data));
      if (data.issues) {
        // eslint-disable-next-line no-console
        console.error("❌ Failed to parse WebSocket message");
        return;
      }

      if (data.output.action === "notification.pong" || data.output.action === "authentication") {
        return;
      }

      // eslint-disable-next-line no-console
      console.log("📩 Message received", data);
      this.dispatchEvent(new CustomEvent(data.output.action, { detail: data.output }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ Failed to parse WebSocket message", error);
    }
  }

  private onClose(event: CloseEvent) {
    // Ignore events from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }
    this.handleDisconnection("Connection closed. Attempting to reconnect...");
  }

  private onError(event: Event) {
    // Ignore events from stale WebSocket connections
    if (event.target !== this.ws) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error("❌ WebSocket error", event);
    this.ws?.close();
  }

  private handleDisconnection(reason: string, error?: unknown) {
    const level = error ? "error" : "log";
    // eslint-disable-next-line no-console
    console[level](`⚠️ ${reason}`, error ?? "");
    this.cleanup();
    if (this.shouldReconnect) {
      this.scheduleReconnect();
    }
  }

  private startPing() {
    this.stopPing();

    this.pingTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: "notification.ping" }));
      }
    }, this.pingInterval);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private scheduleReconnect() {
    this.reconnectTimeout = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log(`🔁 Reconnecting...`);
      void this.connect();
    }, this.reconnectDelay);
  }

  private cleanup() {
    this.stopPing();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  public send(data: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else {
      // eslint-disable-next-line no-console
      console.warn("⚠️ Cannot send — WebSocket not open");
    }
  }

  public close() {
    // eslint-disable-next-line no-console
    console.log("🔒 Closing connection manually...");
    this.shouldReconnect = false;
    this.cleanup();
    this.ws?.close();
  }
}

const webSocket = new WebSocketClient();

export default webSocket;
